import { useEffect, useRef } from 'react';
import { useToken } from '../common/helper';
import { ShipmentMessage } from '@/apis/shipment';
import { GetWsTicket } from '../apis/auth';
import { getSessionSignal } from '@/common/interceptor/sessionAbortController';

export interface NotificationData {
  type: string;
  message?: string;
  data: ShipmentMessage;
}

/**
 * Hook for establishing WebSocket connection to receive notifications
 *
 * @param onMessage - Optional callback function to handle notification messages
 */
export default function useNotificationsSocket(
  onMessage?: (data: NotificationData) => void
): void {
  const { token, decodedUserToken } = useToken();

  const wsRef = useRef<WebSocket>(null);

  useEffect(() => {
    if (!token) return;

    const decoded = decodedUserToken();
    if (decoded?.is_preview) {
      wsRef.current?.close();
      return;
    }

    const baseDomain = process.env.REACT_APP_WEB_SOCKETS;

    if (!baseDomain) {
      console.error('REACT_APP_WEB_SOCKETS environment variable is not set');
      return;
    }

    const signal = getSessionSignal();
    let cancelled = false;

    const onAbort = () => {
      cancelled = true;
      wsRef.current?.close();
    };

    signal.addEventListener('abort', onAbort, { once: true });

    const connect = async () => {
      let url: string;
      try {
        const res = await GetWsTicket();
        const ticket = res?.data?.ticket;
        if (cancelled || signal.aborted) return;
        url = `${baseDomain}/shipment/coc-notifications/?ticket=${ticket}`;
      } catch (error: unknown) {
        const err = error as { code?: string; name?: string; message?: string };
        if (
          cancelled ||
          signal.aborted ||
          err?.code === 'ERR_CANCELED' ||
          err?.name === 'CanceledError' ||
          err?.message?.toLowerCase().includes('canceled') ||
          err?.message?.toLowerCase().includes('cancelled')
        ) {
          return;
        }
        url = `${baseDomain}/shipment/coc-notifications/?token=${token}`;
      }
      if (cancelled || signal.aborted) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected....');
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as NotificationData;
          if (data.type === 'notification') {
            onMessage?.(data);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
      };
    };

    connect();

    return () => {
      cancelled = true;
      signal.removeEventListener('abort', onAbort);
      wsRef.current?.close();
    };
  }, [onMessage, token, decodedUserToken]);
}
